/**
 * The season's small rules on the page side - `EX-INS01`, `docs/29` §6.25.
 *
 * The server folds the table and decides every number; these only read the
 * points rule back in words and turn the editor's text into the body the
 * route bounds, so a mistake is named beside its field before the save.
 */
import type { EditorSeason, SeasonInput, SeasonPoints, SeasonRound } from "../types";

export const SEASON_ROUNDS_MAX = 12;
export const SEASON_POINTS_TABLE_MAX = 50;
export const SEASON_POINTS_MAX = 1000;
export const SEASON_TITLE_MAX = 120;
export const SEASON_DESCRIPTION_MAX = 600;
export const SEASON_LABEL_MAX = 40;

/** The canvas's example, the editor's starting point. */
export const SEASON_DEFAULT_POINTS: SeasonPoints = { table: [100, 80, 65, 55, 50], thenMinus: 1 };

/** The points a place earns, the server's rule, for the editor's preview line. */
export function seasonPointsFor(place: number, points: SeasonPoints): number {
  if (!Number.isInteger(place) || place < 1 || points.table.length === 0) return 0;
  const index = place - 1;
  if (index < points.table.length) return points.table[index]!;
  const last = points.table[points.table.length - 1]!;
  return Math.max(0, last - points.thenMinus * (index - (points.table.length - 1)));
}

export type PointsTableParse = { ok: true; table: number[] } | { ok: false; reason: "empty" | "number" | "rises" | "long" };

/** "100, 80, 65" (commas, spaces or Bangla digits) to a table that never rises. */
export function parsePointsTable(text: string): PointsTableParse {
  const latin = text.replace(/[০-৯]/g, (digit) => String("০১২৩৪৫৬৭৮৯".indexOf(digit)));
  const parts = latin.split(/[\s,]+/).filter(Boolean);
  if (parts.length === 0) return { ok: false, reason: "empty" };
  if (parts.length > SEASON_POINTS_TABLE_MAX) return { ok: false, reason: "long" };
  const table: number[] = [];
  for (const part of parts) {
    if (!/^\d+$/.test(part)) return { ok: false, reason: "number" };
    const value = Number(part);
    if (value > SEASON_POINTS_MAX) return { ok: false, reason: "number" };
    if (table.length > 0 && value > table[table.length - 1]!) return { ok: false, reason: "rises" };
    table.push(value);
  }
  return { ok: true, table };
}

/** The label a round's chip prints: the organizer's words, else "Round <n>". */
export function roundLabel(round: Pick<SeasonRound, "label_en" | "label_bn">, index: number, locale: string, fallback: (n: number) => string): string {
  const own = locale === "bn" ? round.label_bn || round.label_en : round.label_en;
  return own || fallback(index + 1);
}

/** The editor's body from a stored season, unchanged. */
export function seasonInputOf(season: EditorSeason): SeasonInput {
  return {
    title_en: season.title_en,
    title_bn: season.title_bn,
    description_en: season.description_en,
    description_bn: season.description_bn,
    participation: season.participation,
    rounds: season.rounds.map((round) => ({
      contestId: round.contestId,
      label_en: round.label_en,
      label_bn: round.label_bn,
    })),
    points: { table: [...season.points.table], thenMinus: season.points.thenMinus },
    bestOf: season.bestOf,
    published: season.published,
  };
}

/** A new season that starts with this round. */
export function newSeasonInput(contest: { _id: string; title_en: string; participation: "individual" | "team" }): SeasonInput {
  return {
    title_en: "",
    title_bn: null,
    description_en: null,
    description_bn: null,
    participation: contest.participation,
    rounds: [{ contestId: contest._id, label_en: null, label_bn: null }],
    points: { ...SEASON_DEFAULT_POINTS, table: [...SEASON_DEFAULT_POINTS.table] },
    bestOf: null,
    published: false,
  };
}

/** The first two letters of an institution's words, its mark beside the name. */
export function institutionMark(name: string): string {
  const words = name.trim().split(/\s+/).filter((word) => /\p{L}/u.test(word[0] ?? ""));
  const letters = (words.length >= 2 ? [words[0]![0], words[1]![0]] : [...(words[0] ?? name)].slice(0, 2)).join("");
  return letters.toLocaleUpperCase();
}
